import './playerView.css';
import '../documents/documents.css';

import { useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { userConfigState } from '../../recoil/atoms/userConfigState';
import { selectedPlayerState } from '../../recoil/atoms/selectedPlayerState';
import { navigationPropsState } from '../../recoil/atoms/navigationPropsState';
import { useSetSelectedPlayer } from '../../recoil/hooks/useSetSelectedPlayer';
import { useOpenGlobalModal } from '../../recoil/hooks/useOpenGlobalModal';

import NoteAddIcon from '@mui/icons-material/NoteAdd';
import PostAddIcon from '@mui/icons-material/PostAdd';
import AddCommentIcon from '@mui/icons-material/AddComment';
import AttachFileIcon from '@mui/icons-material/AttachFile';

import { translate } from '../../../common/language/translations';
import { ViewContainerBackgroundSvg } from '../../../assets/svg/ViewContainerBackgroundSvg';
import { Report } from '../documents/report/Report';
import { ReportScores } from '../documents/report/ReportScores';
import { TextButton } from '../controls/buttons/TextButton';
import { ExistingReports } from '../documents/existingDocuments/ExistingReports';
import { PlayerReport, ReportType } from '../../types/report';
import { StringToNumberMap, StringToStringToNumberMap } from '../../types/common';
import { DropDown } from '../controls/dropDowns/DropDown';


interface PlayerViewReportsProps {
  reports: PlayerReport[];
  setReports: (newReports: PlayerReport[]) => void;
  averageScoutingReportScores: StringToStringToNumberMap;
  scoutingReportVersionCounts: StringToNumberMap;
  averageMatchReportScores: StringToStringToNumberMap | undefined;
  playerViewIsExpanding: boolean;
}


export const PlayerViewReports: React.FC<PlayerViewReportsProps> = ({
  reports,
  setReports,
  averageScoutingReportScores,
  scoutingReportVersionCounts,
  averageMatchReportScores,
  playerViewIsExpanding,
}) => {

  const userConfig = useRecoilValue(userConfigState);

  const navigationProps = useRecoilValue(navigationPropsState);
  const selectedPlayer = useRecoilValue(selectedPlayerState);
  const { updateSelectedPlayer } = useSetSelectedPlayer();

  const { openConfirmModal } = useOpenGlobalModal();

  const [reportsToShow, setReportsToShow] = useState<PlayerReport[]>([]);
  const [newReportType, setNewReportType] = useState<ReportType | undefined>(undefined);

  const sortedReportVersions = useMemo(() => {
    return Object.keys(scoutingReportVersionCounts).sort((a, b) => scoutingReportVersionCounts[b] - scoutingReportVersionCounts[a]);
  }, [scoutingReportVersionCounts]);

  const [selectedScoutingReportVersion, setSelectedScoutingReportVersion] = useState<string | undefined>(
    sortedReportVersions.length > 0 ? sortedReportVersions[0] : undefined
  );

  const [reportVersionDropDownExpanded, setReportVersionDropDownExpanded] = useState(false);


  const openConfirmLeaveReportModal = (action: () => void) => {
    openConfirmModal(
      action,
      'exitReport?',
      'exitReportInfo'
    );
  };


  const handleCloseReport = () => {
    setNewReportType(undefined);
    updateSelectedPlayer(selectedPlayer, { selectedReport: undefined, reportHasUnsavedChanges: false });
  };

  const handleCloseIconClick = () => {
    if (selectedPlayer?.reportHasUnsavedChanges) {
      openConfirmLeaveReportModal(handleCloseReport);
    }
    else {
      handleCloseReport();
    }
  };


  const handleSelectReport = (report: PlayerReport | undefined) => {
    setNewReportType(undefined);
    updateSelectedPlayer(selectedPlayer, { selectedReport: report, reportHasUnsavedChanges: false });
  };

  const handleReportClicked = (report: PlayerReport | undefined) => {
    if (selectedPlayer?.reportHasUnsavedChanges) {
      openConfirmLeaveReportModal(() => handleSelectReport(report));
    }
    else {
      handleSelectReport(report);
    }
  };


  const addOrUpdateReport = (
    report: PlayerReport,
    isNewReport: boolean,
  ) => {
    let reportWasDeleted = false;

    if (isNewReport) {
      setReports([report, ...reports]);
      setNewReportType(undefined);
    }
    else {
      const updatedReports = reports.map((existingReport: PlayerReport) => {
        if (existingReport.id === report.id) {

          if (!existingReport.isDeleted && report.isDeleted) {
            reportWasDeleted = true;
          }

          return report;
        }

        return existingReport;
      });

      setReports(updatedReports);
    }

    updateSelectedPlayer(selectedPlayer, { selectedReport: reportWasDeleted ? undefined : report, reportHasUnsavedChanges: false });
  };


  useEffect(() => {
    if (!selectedScoutingReportVersion && sortedReportVersions.length > 0) {
      setSelectedScoutingReportVersion(sortedReportVersions[0]);
    }
  }, [sortedReportVersions]); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    if (navigationProps.showDeletedReports) {
      setReportsToShow(reports);
    }
    else {
      setReportsToShow(reports.filter(report => !report.isDeleted));
    }
  }, [reports, navigationProps.showDeletedReports]); // eslint-disable-line react-hooks/exhaustive-deps


  return (
    <div className='full-size-container'>

      {reportVersionDropDownExpanded && <div className='filter-section-empty-background' onClick={() => setReportVersionDropDownExpanded(false)} />}

      <div
        className='player-view-widget-container archive-existing-reports-container'
        style={{ top: '1vh', zIndex: 1 }}
      >
        {!playerViewIsExpanding && (
          <ExistingReports
            reports={reportsToShow}
            selectedReport={selectedPlayer?.selectedReport}
            handleReportClicked={handleReportClicked}
            isPlayerView={true}
            reportsExist={reports.length > 0}
          />
        )}
      </div>

      <div
        className={
          'player-view-widget-container archive-report-container' +
          (selectedPlayer?.selectedReport ? ' player-view-reports-right-section-existing-report' : '')
        }
        style={{ top: '1vh' }}
      >

        <div className='full-size'>
          <ViewContainerBackgroundSvg />
        </div>

        {!newReportType && !selectedPlayer?.selectedReport && (
          <div className='document-content-section'>

            <div className='report-content-left-column'>
              <div className='player-view-reports-new-report-buttons-column'>
                <TextButton
                  onClick={() => setNewReportType('ScoutingReport')}
                  text={translate('newScoutingReport', userConfig?.language)}
                  icon={<NoteAddIcon style={{ fontSize: 22 }} />}
                  buttonType={'transparent'}
                  isRound={true}
                  style={{ width: 210, height: 36 }}
                />

                <TextButton
                  onClick={() => setNewReportType('MatchReport')}
                  text={translate('newMatchReport', userConfig?.language)}
                  icon={<PostAddIcon style={{ fontSize: 22 }} />}
                  buttonType={'transparent'}
                  isRound={true}
                  style={{ width: 210, height: 36 }}
                />

                <TextButton
                  onClick={() => setNewReportType('Comment')}
                  text={translate('newComment', userConfig?.language)}
                  icon={<AddCommentIcon style={{ fontSize: 22 }} />}
                  buttonType={'transparent'}
                  isRound={true}
                  style={{ width: 210, height: 36 }}
                />

                <TextButton
                  onClick={() => setNewReportType('Attachment')}
                  text={translate('newAttachment', userConfig?.language)}
                  icon={<AttachFileIcon style={{ fontSize: 22 }} />}
                  buttonType={'transparent'}
                  isRound={true}
                  style={{ width: 210, height: 36 }}
                />
              </div>
            </div>

            {selectedScoutingReportVersion && (
              <div className='report-content-right-column'>
                <div
                  className={'report-content-average-title' + (sortedReportVersions.length > 1 ? ' flex-column align-center' : '')}
                  style={{ marginTop: sortedReportVersions.length > 1 ? -36 : undefined }}
                >
                  {translate(sortedReportVersions.length > 1 ? 'averageOfScoutingReports' : 'averageOfAllScoutingReports', userConfig?.language)}
                  {' (' + scoutingReportVersionCounts[selectedScoutingReportVersion] + ')'}

                  {sortedReportVersions.length > 1 && (
                    <div className='report-version-drop-down-container' style={{ height: 22, marginTop: 12, width: 235 }}>
                      <DropDown
                        id={'report-version-select'}
                        dropDownOptions={sortedReportVersions}
                        selectedOptions={[selectedScoutingReportVersion]}
                        setSelectedOptions={(value: string[]) => setSelectedScoutingReportVersion(value[0])}
                        isDropDownExpanded={reportVersionDropDownExpanded}
                        setIsDropDownExpanded={(value: boolean) => setReportVersionDropDownExpanded(value)}
                        defaultDropDownText={'select'}
                        defaultDropDownTextColor={'#000000'}
                        marginBetweenOptions={3}
                        maxHeight={'60vh'}
                        height={22}
                        forceSelection={true}
                        isReportVersionDropDown={true}
                      />
                    </div>
                  )}
                </div>

                <ReportScores
                  reportVersion={selectedScoutingReportVersion}
                  scoreValues={averageScoutingReportScores[selectedScoutingReportVersion]}
                />
              </div>
            )}
          </div>
        )}

        {(newReportType || selectedPlayer?.selectedReport) && selectedPlayer?.id && selectedPlayer?.name && (
          <div className='full-size-container'>
            <Report
              newReportType={newReportType}
              selectedReport={selectedPlayer?.selectedReport}

              handleCloseIconClick={handleCloseIconClick}
              addOrUpdateReport={addOrUpdateReport}

              reportVersionDropDownExpanded={reportVersionDropDownExpanded}
              setReportVersionDropDownExpanded={setReportVersionDropDownExpanded}

              playerOverview={selectedPlayer?.playerOverview}
              playerEntry={{ id: selectedPlayer?.id, name: selectedPlayer?.name }}

              averageMatchReportScores={averageMatchReportScores}
            />
          </div>
        )}
      </div>
    </div>
  );
};
